import { useState } from 'react';
import Modal from '../common/Modal';
import Button from '../common/Button';
import styles from './PaymentDeclineModal.module.css';

/**
 * Payment Decline Modal
 * Displays user-friendly error messages for Authorize.net payment failures
 */

// Map Authorize.net error codes to user-friendly messages
const getErrorMessage = (errorCode, errorMessage) => {
  const errorCodeMap = {
    // Card declined errors
    '2': 'Your card was declined. Please try a different payment method or contact your bank.',
    '3': 'This transaction requires authorization from your bank. Please contact your bank or try a different card.',
    '4': 'This card has been flagged for possible fraud. Please contact your bank or try a different payment method.',
    '27': 'The billing address provided does not match your card. Please verify and try again.',
    '28': 'Your card issuer does not support this transaction type. Please try a different card.',
    '44': 'Your card\'s security code (CVV) does not match. Please verify and try again.',
    '45': 'The card\'s expiration date is invalid. Please check the date and try again.',
    '127': 'The AVS check failed. Please verify your billing address matches your card.',
    '254': 'Your transaction was declined. Please contact your bank for more information.',

    // Insufficient funds
    '5': 'Your card has insufficient funds. Please try a different payment method.',

    // Invalid card
    '6': 'The card number is invalid. Please check and try again.',
    '7': 'The expiration date is invalid. Please check and try again.',
    '8': 'The card has expired. Please use a different card.',
    '17': 'Your card does not support this type of purchase. Please try a different card.',
    '37': 'The card number does not match the card type. Please verify your card details.',

    // Processing errors
    '11': 'A duplicate transaction has been submitted. Please wait a moment and check your order history.',
    '141': 'This transaction has been declined too many times. Please try again later or use a different card.',

    // General network/system errors
    'NETWORK_ERROR': 'We\'re having trouble connecting to the payment processor. Please check your internet connection and try again.',
    'PAYMENT_FAILED': 'The payment could not be processed. Please try again or use a different payment method.',
    'CAPTURE_FAILED': 'There was an issue completing your payment. Please contact support for assistance.',
    'VOID_FAILED': 'There was an issue canceling the transaction. Please contact support for assistance.',
    'REFUND_FAILED': 'There was an issue processing your refund. Please contact support for assistance.',
    // Card #54415 (D3). Checkout aborts its own submit after a deadline; that is NOT a decline,
    // and the honest copy has to say the outcome is unknown while keeping Retry available.
    'REQUEST_TIMEOUT': 'We did not hear back from the payment processor in time. Your card may not have been charged. Check your order history, or try again — a duplicate order is detected automatically.'
  };

  return errorCodeMap[errorCode] || errorMessage || 'Your payment could not be processed. Please try again or contact support.';
};

const getErrorTitle = (errorCode) => {
  if (errorCode === 'NETWORK_ERROR') return 'Connection Error';
  if (errorCode === 'REQUEST_TIMEOUT') return 'Request Timed Out';
  if (['2', '3', '4', '254'].includes(errorCode)) return 'Card Declined';
  if (errorCode === '5') return 'Insufficient Funds';
  if (['6', '7', '8', '17', '37'].includes(errorCode)) return 'Invalid Card';
  if (errorCode === '11') return 'Duplicate Transaction';
  return 'Payment Failed';
};

const PaymentDeclineModal = ({
  isOpen,
  onClose,
  errorCode,
  errorMessage,
  onRetry,
  onContactSupport,
  onTryDifferentCard
}) => {
  const [isRetrying, setIsRetrying] = useState(false);

  const title = getErrorTitle(errorCode);
  const message = getErrorMessage(errorCode, errorMessage);

  const handleRetry = async () => {
    setIsRetrying(true);
    try {
      await onRetry?.();
    } finally {
      setIsRetrying(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={title} size="medium">
      <div className={styles.content}>
        {/* Error Icon */}
        <div className={styles.iconContainer}>
          <svg
            className={styles.errorIcon}
            width="64"
            height="64"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="8" x2="12" y2="12" />
            <line x1="12" y1="16" x2="12.01" y2="16" />
          </svg>
        </div>

        {/* Error Message */}
        <p className={styles.message}>{message}</p>

        {/* Suggested Actions */}
        <div className={styles.suggestions}>
          <h3>What can you do?</h3>
          <ul>
            {['2', '3', '4', '5', '254'].includes(errorCode) && (
              <>
                <li>Try a different payment method</li>
                <li>Contact your bank for more information</li>
              </>
            )}
            {['6', '7', '8', '37', '44', '45'].includes(errorCode) && (
              <>
                <li>Double-check your card details</li>
                <li>Try a different card</li>
              </>
            )}
            {errorCode === '27' || errorCode === '127' && (
              <li>Verify your billing address matches your card</li>
            )}
            {errorCode === '11' && (
              <li>Wait a few minutes before trying again</li>
            )}
            {errorCode === 'NETWORK_ERROR' && (
              <>
                <li>Check your internet connection</li>
                <li>Try again in a moment</li>
              </>
            )}
            <li>Contact our support team if the issue persists</li>
          </ul>
        </div>

        {/* Action Buttons */}
        <div className={styles.actions}>
          {onRetry && (
            <Button
              onClick={handleRetry}
              loading={isRetrying}
              fullWidth
            >
              Try Again
            </Button>
          )}

          {onTryDifferentCard && (
            <Button
              onClick={onTryDifferentCard}
              variant="outline"
              fullWidth
            >
              Use Different Card
            </Button>
          )}

          {onContactSupport && (
            <Button
              onClick={onContactSupport}
              variant="outline"
              fullWidth
            >
              Contact Support
            </Button>
          )}

          <Button
            onClick={onClose}
            variant="ghost"
            fullWidth
          >
            Cancel
          </Button>
        </div>

        {/* Debug info (only shown in development) */}
        {process.env.NODE_ENV === 'development' && errorCode && (
          <div className={styles.debug}>
            <small>Error Code: {errorCode}</small>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default PaymentDeclineModal;
